import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { api } from '../utilities/api';
import { createPasskeyAttestation, isPasskeySupported } from '../utilities/webauthn';

const PASSKEYS_ENABLED = import.meta.env.VITE_ENABLE_PASSKEYS !== 'false';
const APP_VERSION = import.meta.env.VITE_REDIMDWM_APP_VERSION || 'local';
const PROMPT_STORAGE_KEY = 'redimdwmUi_passkeyEnrollmentPrompt';
const NOT_NOW_SESSION_KEY = 'redimdwmUi_passkeyEnrollmentPromptNotNow';
const DONT_ASK_AGAIN_DAYS = 120;

const getStoredPreference = () => {
  try {
    return JSON.parse(localStorage.getItem(PROMPT_STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
};

const shouldSuppressPrompt = () => {
  const preference = getStoredPreference();
  if (!preference) return false;
  if (preference.appVersion !== APP_VERSION) return false;
  return Number(preference.suppressedUntil || 0) > Date.now();
};

function PasskeyEnrollmentPrompt() {
  const { isLoggedIn, loadingSession, user } = useUser();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const checkedUserIdRef = useRef(null);

  useEffect(() => {
    if (!PASSKEYS_ENABLED || !isPasskeySupported()) {
      return;
    }
    if (!isLoggedIn || loadingSession || !user?.userid) {
      return;
    }
    if (location.pathname !== '/dashboard') {
      return;
    }
    if (sessionStorage.getItem(NOT_NOW_SESSION_KEY) === APP_VERSION) {
      return;
    }
    if (shouldSuppressPrompt()) {
      return;
    }
    if (checkedUserIdRef.current === user.userid) {
      return;
    }

    let cancelled = false;
    const checkPasskeys = async () => {
      checkedUserIdRef.current = user.userid;
      const result = await api.listPasskeys();
      if (cancelled) return;
      if (result?.success && (result.passkeys || []).length === 0) {
        setVisible(true);
      } else if (!result?.success) {
        checkedUserIdRef.current = null;
      }
    };

    checkPasskeys();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, loadingSession, location.pathname, user?.userid]);

  const dismissForSession = () => {
    sessionStorage.setItem(NOT_NOW_SESSION_KEY, APP_VERSION);
    setVisible(false);
  };

  const dismissLongTerm = () => {
    const suppressedUntil = Date.now() + DONT_ASK_AGAIN_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(PROMPT_STORAGE_KEY, JSON.stringify({
      appVersion: APP_VERSION,
      suppressedUntil,
    }));
    setVisible(false);
  };

  const handleEnroll = async () => {
    setLoading(true);
    try {
      const options = await api.getPasskeyRegisterOptions({
        origin: window.location.origin,
      });
      if (options?.error) throw new Error(options.error);

      const attestation = await createPasskeyAttestation(options);
      const result = await api.verifyPasskeyRegister({
        challengeId: options.challengeId,
        challenge: options.publicKey?.challenge || options.challenge,
        attestation,
        device_label: 'Primary device',
        origin: window.location.origin,
      });

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to set up passkey.');
      }

      localStorage.removeItem(PROMPT_STORAGE_KEY);
      setVisible(false);
      toast.success('Passkey set up for next time.');
    } catch (err) {
      toast.error(err?.message || 'Passkey setup failed.');
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div
        className="w-full max-w-md rounded-md border border-primary/30 bg-white p-5 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="passkey-enrollment-title"
      >
        <div className="space-y-4">
        <div>
          <h2 id="passkey-enrollment-title" className="text-base font-semibold text-primary">
            Set up a passkey for next time
          </h2>
          <p className="mt-1 text-sm text-neutral-darkest">
            Sign in faster using this device instead of typing your password.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <button
            type="button"
            onClick={handleEnroll}
            disabled={loading}
            className="rounded-md bg-button px-3 py-2 text-sm font-medium text-white hover:bg-button-light focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
          >
            {loading ? 'Setting up...' : 'Set up passkey'}
          </button>
          <button
            type="button"
            onClick={dismissForSession}
            disabled={loading}
            className="rounded-md border border-neutral px-3 py-2 text-sm font-medium text-neutral-darkest hover:bg-neutral-lightest focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={dismissLongTerm}
            disabled={loading}
            className="rounded-md border border-neutral px-3 py-2 text-sm font-medium text-neutral-darkest hover:bg-neutral-lightest focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
          >
            Don't ask again
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

export default PasskeyEnrollmentPrompt;
